// Этап 25 — Действия Синтетика (v6 ID). 19 карт.
// Большая часть простых эффектов реализована, сложные mod-chain — стабы.

import { applyDamage } from '../../../combat.js';
import { applyPermanentBuff, applyTemporaryBuff } from '../../../buffs.js';
import { addPermanentKeyword } from '../../../keywords/permanent.js';
import { addTransientKeyword } from '../../../keywords/transient.js';
import { KW } from '../../../keywords.js';
import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { registerC1Order } from '../../global/c1/order-registry.js';
import { generateRandomMod } from './mod-factory.js';
import { attachMod } from '../modifications.js';
import { onUnitDied } from '../../deus/index.js';
import { Card } from '../../../card.js';
import { cards as cardsSyntetics } from '../../../../data/cards-syntetics.js';

function ownAllies(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.field) return [];
  const out = [];
  for (const row of p.field) for (const u of row || []) {
    if (u && u.isAlive && u.isAlive()) out.push(u);
  }
  return out;
}
function ownDiscardUnits(state, playerIdx) {
  return (state.players?.[playerIdx]?.discard || []).filter(c => c?.type === 'unit');
}

// === Сделка: draw 2 + 2 dmg hero_self ===
registerC1Order('syn_act_sdelka', (state, source) => {
  const p = state.players?.[source.ownerId];
  if (!p) return { ok: false };
  for (let i = 0; i < 2; i++) {
    if (p.deck?.length > 0 && p.hand.length < 10) p.hand.push(p.deck.shift());
  }
  if (p.hero) applyDamage(p.hero, 2, { state, source });
  return { ok: true };
});

// === Драчка: +1/0/0 всем своим юнитам до конца хода ===
registerC1Order('syn_act_drachka', (state, source) => {
  const allies = ownAllies(state, source.ownerId);
  const expiresAtTurn = (state.currentTurn ?? 1) + 1;
  for (const u of allies) {
    applyTemporaryBuff(state, u, { atk: 1, armor: 0, hp: 0 }, expiresAtTurn, source.id, 'Драчка');
  }
  return { ok: true, buffed: allies.length };
});

// === Электрокнут (БУГ.22): 2 урона ЛЮБОЙ цели; если союзник и выжил —
// перманентный бафф +2/0/0 до конца игры. Раньше: урон только врагу, бафф
// только своему и на 1 ход — не совпадало с текстом карты «2 урона
// выбранной цели; если цель — союзник, +2/0/0 до конца игры».
registerC1Order('syn_act_elektroknut', (state, source, opts) => {
  const t = opts?.target;
  if (!t) return { ok: false };
  applyDamage(t, 2, { state, source });
  if (t.ownerId === source.ownerId && t.isAlive && t.isAlive()) {
    applyPermanentBuff(state, t, { atk: 2, armor: 0, hp: 0 }, source.id, 'Электрокнут');
  }
  return { ok: true };
});

// === Стим: ally +2/0/1 + frenzy на 2 хода ===
registerC1Order('syn_act_stim', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId !== source.ownerId) return { ok: false };
  const expiresAtTurn = (state.currentTurn ?? 1) + 2;
  applyTemporaryBuff(state, t, { atk: 2, armor: 0, hp: 1 }, expiresAtTurn, source.id, 'Стим');
  // Аудит-фикс: Неистовство ВРЕМЕННОЕ (по тексту «на 2 хода»), а не навсегда.
  addTransientKeyword(t, KW.FRENZY, source.id, expiresAtTurn);
  return { ok: true };
});

// === Стальной кулак: Герой +2/0/0 + frenzy до конца хода ===
registerC1Order('syn_act_stalnoy_kulak', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  const expiresAtTurn = (state.currentTurn ?? 1) + 1;
  applyTemporaryBuff(state, hero, { atk: 2, armor: 0, hp: 0 }, expiresAtTurn, source.id, 'Стальной кулак');
  // Аудит-фикс: Неистовство ВРЕМЕННОЕ (по тексту «до конца хода»), а не навсегда.
  addTransientKeyword(hero, KW.FRENZY, source.id, expiresAtTurn);
  return { ok: true };
});

// === Облава: 1 dmg всем юнитам + 2 dmg своему Герою ===
registerC1Order('syn_act_oblava', (state, source) => {
  const all = [];
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (u && u.isAlive && u.isAlive()) all.push(u);
    }
  }
  for (const u of all) applyDamage(u, 1, { state, source });
  const hero = state.players?.[source.ownerId]?.hero;
  if (hero) applyDamage(hero, 2, { state, source });
  return { ok: true, hit: all.length };
});

// === Бойцовский клуб: всем своим юнитам с Модом +1/0/0 + frenzy до конца хода ===
registerC1Order('syn_act_boytsovsky_klub', (state, source) => {
  const allies = ownAllies(state, source.ownerId)
    .filter(u => Array.isArray(u.modifications) && u.modifications.length > 0);
  const expiresAtTurn = (state.currentTurn ?? 1) + 1;
  for (const u of allies) {
    applyTemporaryBuff(state, u, { atk: 1, armor: 0, hp: 0 }, expiresAtTurn, source.id, 'Бойцовский клуб');
    // Аудит-фикс: Неистовство ВРЕМЕННОЕ (по тексту «до конца хода»), а не навсегда.
    addTransientKeyword(u, KW.FRENZY, source.id, expiresAtTurn);
  }
  return { ok: true, buffed: allies.length };
});

// === Имплант-конвейер: всем своим юнитам без модов 1 random Мод ===
registerC1Order('syn_act_implant_konveyer', (state, source) => {
  const allies = ownAllies(state, source.ownerId)
    .filter(u => !u.modifications || u.modifications.length === 0);
  for (const u of allies) {
    generateRandomMod(state, source.ownerId, { destination: 'attach', target: u });
  }
  return { ok: true, applied: allies.length };
});

// === Шедевр инженерии: random Mod из колоды → attach на цель ===
registerC1Order('syn_act_shedevr_inzhenerii', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId !== source.ownerId) return { ok: false };
  const p = state.players[source.ownerId];
  const modsInDeck = (p.deck || []).filter(c => c?.type === 'mod');
  if (modsInDeck.length > 0) {
    const pick = modsInDeck[state.rng?.int ? state.rng.int(modsInDeck.length) : 0];
    const dIdx = p.deck.indexOf(pick);
    if (dIdx >= 0) p.deck.splice(dIdx, 1);
    attachMod(state, t, { id: pick.id, name_ru: pick.name_ru }, { isPlayed: false });
  } else {
    generateRandomMod(state, source.ownerId, { destination: 'attach', target: t });
  }
  return { ok: true };
});

// === 2d reveal-pick (Дозаправка / Конструктор имплантов) ===
// Вскрыть look случайных Модов из колоды → выбрать pick → выбранные в руку,
// остальные вскрытые в сброс. Выбор игрока приходит из UI через
// opts.{revealedInstanceIds, pickedInstanceIds}. Без выбора (бот/fallback) —
// случайный pick (НЕ «все в руку», как было ошибочно — теперь по тексту карты).
//
// Вскрытие (какие именно look Модов) для UI делает revealModsFromDeck в Ф2
// (PvE — локально, PvP — на сервере для анти-чит RNG); сюда (Ф3) приходят их id.

// Peek: вернуть count случайных Мод-карт из колоды (НЕ удаляя — удалит резолвер в Ф3).
export function revealModsFromDeck(state, idx, count) {
  const p = state.players?.[idx];
  if (!p?.deck) return [];
  const pool = p.deck.filter(c => c?.type === 'mod');
  const out = [];
  const n = Math.min(count, pool.length);
  for (let i = 0; i < n; i++) {
    const j = state.rng?.int ? state.rng.int(pool.length) : 0;
    out.push(pool.splice(j, 1)[0]);
  }
  return out;
}

function resolveModRevealPick(state, idx, look, pick, opts = {}) {
  const p = state.players?.[idx];
  if (!p?.deck) return { ok: false };
  const modsInDeck = p.deck.filter(c => c?.type === 'mod');
  if (modsInDeck.length === 0) return { ok: true, looked: 0 };
  // Вскрытые: из UI (opts) или случайно (fallback/бот).
  let revealed;
  if (Array.isArray(opts.revealedInstanceIds) && opts.revealedInstanceIds.length > 0) {
    const set = new Set(opts.revealedInstanceIds);
    revealed = modsInDeck.filter(c => set.has(c.instanceId));
  } else {
    revealed = revealModsFromDeck(state, idx, look);
  }
  if (revealed.length === 0) return { ok: true, looked: 0 };
  // Выбранные: из UI или случайно (pick шт.).
  let pickedSet;
  if (Array.isArray(opts.pickedInstanceIds)) {
    pickedSet = new Set(opts.pickedInstanceIds.filter(id => revealed.some(c => c.instanceId === id)));
  } else {
    const shuffled = [...revealed];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = state.rng?.int ? state.rng.int(i + 1) : 0;
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    pickedSet = new Set(shuffled.slice(0, pick).map(c => c.instanceId));
  }
  let toHand = 0, toDiscard = 0;
  for (const mod of revealed) {
    const dIdx = p.deck.indexOf(mod);
    if (dIdx >= 0) p.deck.splice(dIdx, 1);
    if (pickedSet.has(mod.instanceId) && p.hand.length < 10) {
      p.hand.push(mod); toHand++;
    } else {
      (p.discard || (p.discard = [])).push(mod); toDiscard++;
    }
  }
  state.log?.add(`🔧 ${p.name}: вскрыто ${revealed.length} Мод(ов) → ${toHand} в руку, ${toDiscard} в сброс`);
  return { ok: true, toHand, toDiscard };
}

// === Дозаправка: вскрыть 3 Мода → выбрать 1 в руку, 2 в сброс ===
registerC1Order('syn_act_dozapravka', (state, source, opts) =>
  resolveModRevealPick(state, source.ownerId, 3, 1, opts || {}));

// === Реквизиция: уничтожить своего юнита, добор 2, +1 случайный Мод ===
registerC1Order('syn_act_rekvizitsiya', (state, source, opts) => {
  const p = state.players[source.ownerId];
  const t = opts?.target;
  if (t && t.ownerId === source.ownerId && t.isAlive && t.isAlive()) {
    // БАГ-фикс (Этап 56.46): правильная смерть (removeFromField + onUnitDied),
    // а не applyDamage(999) — Посмертие жертвы срабатывает, слот освобождается.
    t.alive = false;
    p.removeFromField(t);
    onUnitDied(state, { ownerIdx: source.ownerId, card: t, killer: null, killerIdx: null, killerWasCounter: false });
  }
  for (let i = 0; i < 2; i++) {
    if (p.deck?.length > 0 && p.hand.length < 10) p.hand.push(p.deck.shift());
  }
  generateRandomMod(state, source.ownerId, { destination: 'hand' });
  return { ok: true };
});

// === Жертвенный круг: уничтожить всех своих юнитов с atk ≤ 2; за каждого —
// добор 1 карты И 2 урона случайному вражескому юниту ===
// БАГ-фикс (Этап 56.46): раньше давал Герою +atk за каждого уничтоженного
// (НЕ по тексту карты) и убивал через applyDamage(999) без снятия с поля.
registerC1Order('syn_act_zhertvenny_krug', (state, source) => {
  const me = state.players?.[source.ownerId];
  const enemy = state.players?.[1 - source.ownerId];
  if (!me || !enemy) return { ok: false };
  const allies = ownAllies(state, source.ownerId).filter(u => (u.current?.atk ?? 99) <= 2);
  let count = 0;
  for (const u of allies) {
    u.alive = false;
    me.removeFromField(u);
    onUnitDied(state, { ownerIdx: source.ownerId, card: u, killer: null, killerIdx: null, killerWasCounter: false });
    count++;
  }
  state.log?.add(`⭕ Жертвенный круг [${me.name}]: уничтожено ${count} юнитов (atk≤2)`);
  // За каждого уничтоженного: добор 1 + 2 урона случайному вражескому юниту.
  for (let i = 0; i < count; i++) {
    if (me.deck?.length > 0 && me.hand.length < 10) me.hand.push(me.deck.shift());
    const enemies = enemy.field.flat().filter(u => u && u.isAlive && u.isAlive());
    if (enemies.length > 0) {
      const t = enemies[state.rng?.int ? state.rng.int(enemies.length) : 0];
      applyDamage(t, 2, { state, source });
    }
  }
  return { ok: true, sacrificed: count };
});

// === Печь: уничтожить всех своих юнитов на выбранной линии; сумма их атак
// распределяется по 1 урону по случайным вражеским ПЕРСОНАЖАМ (юниты + Герой,
// Авангард не блокирует) ===
// БАГ-фикс (Этап 56.46): раньше давал Герою +totalAtk (НЕ по тексту карты).
registerC1Order('syn_act_pech', (state, source, opts) => {
  const line = opts?.chosenLine ?? 0;
  const me = state.players?.[source.ownerId];
  const enemy = state.players?.[1 - source.ownerId];
  if (!me?.field || !enemy) return { ok: false };
  const row = me.field[line] || [];
  let totalAtk = 0;
  const toKill = [];
  for (const u of row) {
    if (!u || !u.isAlive || !u.isAlive()) continue;
    totalAtk += u.current?.atk ?? 0;
    toKill.push(u);
  }
  for (const u of toKill) {
    u.alive = false;
    me.removeFromField(u);
    onUnitDied(state, { ownerIdx: source.ownerId, card: u, killer: null, killerIdx: null, killerWasCounter: false });
  }
  state.log?.add(`🔥 Печь [${me.name}]: линия ${line + 1} уничтожена, сумма атак ${totalAtk} → по врагам`);
  // totalAtk ударов по 1 урону по случайным вражеским персонажам (юниты + Герой).
  for (let i = 0; i < totalAtk; i++) {
    const persons = enemy.field.flat().filter(u => u && u.isAlive && u.isAlive());
    if (enemy.hero && enemy.hero.current.hp > 0) persons.push(enemy.hero);
    if (persons.length === 0) break;
    const t = persons[state.rng?.int ? state.rng.int(persons.length) : 0];
    applyDamage(t, 1, { state, source });
  }
  return { ok: true, sacrificed: toKill.length, atkDistributed: totalAtk };
});

// === Конструктор имплантов: вскрыть 4 Мода → выбрать 2 в руку, 2 в сброс ===
registerC1Order('syn_act_konstruktor_implantov', (state, source, opts) =>
  resolveModRevealPick(state, source.ownerId, 4, 2, opts || {}));

// === Боевой стенд: разыграть до 3 Модов из руки на одну цель бесплатно ===
// Упрощённо: attach все Mod-карты из руки на target (до 3 шт).
registerC1Order('syn_act_boevoy_stend', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId !== source.ownerId) return { ok: false };
  const p = state.players[source.ownerId];
  let attached = 0;
  const mods = (p.hand || []).filter(c => c?.type === 'mod');
  for (const m of mods) {
    if (attached >= 3) break;
    const idx = p.hand.indexOf(m);
    if (idx < 0) continue;
    p.hand.splice(idx, 1);
    attachMod(state, t, { id: m.id, name_ru: m.name_ru }, { isPlayed: false });
    attached++;
  }
  return { ok: true, attached };
});

// === Имплантация в подвале: до 2 Модов из руки на цель бесплатно ===
registerC1Order('syn_act_implantatsia_v_podvale', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId !== source.ownerId) return { ok: false };
  const p = state.players[source.ownerId];
  let attached = 0;
  const mods = (p.hand || []).filter(c => c?.type === 'mod');
  for (const m of mods) {
    if (attached >= 2) break;
    const idx = p.hand.indexOf(m);
    if (idx < 0) continue;
    p.hand.splice(idx, 1);
    attachMod(state, t, { id: m.id, name_ru: m.name_ru }, { isPlayed: false });
    attached++;
  }
  return { ok: true, attached };
});

// === Перепрошивка: удвоить Моды на одной цели (упрощённо: продублировать ID) ===
registerC1Order('syn_act_pereproshivka', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId !== source.ownerId) return { ok: false };
  if (!Array.isArray(t.modifications) || t.modifications.length === 0) {
    return { ok: false, reason: 'no_mods' };
  }
  const snapshot = [...t.modifications];
  let added = 0;
  for (const m of snapshot) {
    const r = attachMod(state, t, { id: m.id, name_ru: m.name_ru }, { isPlayed: false });
    if (r.ok) added++;
    if (t.overloaded) break; // Перегрузка остановит цепочку.
  }
  return { ok: true, added };
});

// === Большой передел: условие 4+ союзников с модами → board wipe ===
registerC1Order('syn_act_bolshoy_peredel', (state, source) => {
  const p = state.players?.[source.ownerId];
  if (!p?.field) return { ok: false };
  // Условие: 4+ союзников с хотя бы 1 Модом.
  let withMods = 0;
  for (const row of p.field) for (const u of row || []) {
    if (u && u.isAlive && u.isAlive()
      && Array.isArray(u.modifications) && u.modifications.length > 0) {
      withMods++;
    }
  }
  if (withMods < 4) {
    state.log?.add(`⚙ Большой передел: условие не выполнено (нужно 4+ юнитов с Модом, есть ${withMods}) — карта сгорает`);
    return { ok: true, fizzled: true };
  }
  // Board wipe: все юниты обеих сторон.
  let killed = 0;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const sp = state.players[pIdx];
    if (!sp?.field) continue;
    // Снимок ссылок до применения урона.
    const snapshot = [];
    for (const row of sp.field) for (const u of row || []) {
      if (u && u.isAlive && u.isAlive()) snapshot.push(u);
    }
    for (const u of snapshot) {
      // БАГ-фикс (Этап 56.46): правильная смерть — removeFromField + onUnitDied
      // (Посмертия срабатывают: Эксп. батарея, Гаспер и пр.), а не applyDamage(999).
      if (!u.isAlive || !u.isAlive()) continue; // мог уже погибнуть от каскада
      u.alive = false;
      sp.removeFromField(u);
      onUnitDied(state, { ownerIdx: pIdx, card: u, killer: null, killerIdx: null, killerWasCounter: false });
      killed++;
    }
  }
  state.log?.add(`⚙ Большой передел: уничтожено ${killed} юнитов на поле`);
  return { ok: true, killed };
});
registerC1Order('syn_act_bonya_v_podvale', (state, source) => {
  // БАГ-фикс (Этап 56.46): раньше «упрощённо» давал frenzy + добор 2 карт. Текст:
  // Герой получает frenzy до конца хода; извлеки 2 случайных Мода из колоды и
  // разыграй на 2 РАЗНЫХ союзников бесплатно (если союзников <2 — на одного;
  // если 0 — только frenzy).
  const p = state.players?.[source.ownerId];
  if (!p) return { ok: false };
  if (p.hero) {
    // Аудит-фикс: Неистовство ВРЕМЕННОЕ (по тексту «до конца хода»), а не навсегда.
    const expiresAtTurn = (state.currentTurn ?? 1) + 1;
    addTransientKeyword(p.hero, KW.FRENZY, source.id, expiresAtTurn);
  }
  const allies = ownAllies(state, source.ownerId);
  if (allies.length === 0) return { ok: true, frenzyOnly: true };
  const modsInDeck = (p.deck || []).filter(c => c?.type === 'mod');
  let attached = 0;
  for (let i = 0; i < 2 && modsInDeck.length > 0; i++) {
    const mIdx = state.rng?.int ? state.rng.int(modsInDeck.length) : 0;
    const [modCard] = modsInDeck.splice(mIdx, 1);
    const dIdx = p.deck.indexOf(modCard);
    if (dIdx >= 0) p.deck.splice(dIdx, 1);
    const target = allies[i % allies.length];   // 2 разных (или один если allies==1)
    attachMod(state, target, modCard.template || modCard);
    attached++;
  }
  state.log?.add(`🔪 Бойня в подвале [${p.name}]: Герой frenzy + ${attached} Мод(ов) на союзников`);
  return { ok: true, attached };
});

// === Регистрация всех 19 Действий ===
const SYN_ACTIONS = [
  'syn_act_boevoy_stend', 'syn_act_bolshoy_peredel', 'syn_act_bonya_v_podvale',
  'syn_act_boytsovsky_klub', 'syn_act_dozapravka', 'syn_act_drachka',
  'syn_act_elektroknut', 'syn_act_implant_konveyer',
  'syn_act_implantatsia_v_podvale', 'syn_act_konstruktor_implantov',
  'syn_act_oblava', 'syn_act_pech', 'syn_act_pereproshivka',
  'syn_act_rekvizitsiya', 'syn_act_sdelka', 'syn_act_shedevr_inzhenerii',
  'syn_act_stalnoy_kulak', 'syn_act_stim', 'syn_act_zhertvenny_krug'
];
for (const id of SYN_ACTIONS) registerImplementedAbility(id, 'order');
